/**
 * Asset lifecycle — the wire shape for retiring a unit.
 *
 * ## Why this file exists (TRBUD-92 / TICKET-818)
 *
 * `set_asset_lifecycle_status()` was built by migration 0058, is SECURITY
 * DEFINER, and `tb_app` has held EXECUTE on it since the day it landed. Its
 * `manage_asset_lifecycle` row in `routine_permission` is seeded AND editable
 * from the live Settings screen. Everything below the API was finished.
 *
 * It had **zero TypeScript callers**. `apps/api/src/routes/assets.ts` carried
 * three GETs, create, mark-down and back-in-service, and no PATCH of any kind.
 * So a fleet asset-management product could not retire, decommission or
 * otherwise dispose of an asset, and every local asset had only ever been
 * `active`. The break was the ROUTE, not the grant — which is the sharpest
 * example of the pattern the 1 September editability audit was named for.
 *
 * ## Do not widen the grant to fix this
 *
 * `tb_app` deliberately holds INSERT/SELECT and **no UPDATE** on `asset.status`,
 * `in_service_at` and `retired_at` (`bootstrap/02-grants.sql`), because those
 * columns belong to the function. The absence is a design decision, not an
 * oversight. Call the function.
 *
 * ## `retired` is TERMINAL, and `inactive` is not part of the machine
 *
 * The function refuses to move an asset out of `retired` — recorded there as a
 * limitation rather than sold as a feature, since a mis-click is currently
 * unfixable. It also refuses to decommission an `inactive` asset without first
 * returning it to `active`, so the lifecycle never starts from a state it does
 * not define. Both refusals surface here as ordinary 409s.
 *
 * ## `disposed` is unreachable BY EVERY PATH, and is out of scope
 *
 * Worth knowing before someone tries to add it to this enum and wonders why
 * nothing works. Three independent things block it and all three would have to
 * move together:
 *
 * 1. `set_asset_lifecycle_status` refuses any status outside
 *    `active | decommissioning | retired`.
 * 2. `asset.disposed_on` has no UPDATE grant below the owner.
 * 3. A biconditional CHECK ties `status = 'disposed'` to `disposed_on` being
 *    set, so the two must move in one statement.
 *
 * Note the comment in `02-grants.sql` claiming the function "enforces that
 * disposed is terminal" was simply WRONG — it refuses `disposed` outright and
 * makes `retired` terminal instead. Corrected in place. Disposal needs its own
 * decision about what it means financially; it is not this route.
 */
import { z } from "zod";
import { idempotencyKey, strictObject } from "./requests.js";

/**
 * The three states the function actually implements.
 *
 * NOT the same set as `asset.status` accepts — `inactive` predates this machine
 * and `disposed` is unreachable (see the header). Naming only what can be
 * reached means an unreachable value is refused by the parser with a readable
 * message instead of by the database with a 23514.
 */
export const assetLifecycleStatuses = ["active", "decommissioning", "retired"] as const;
export type AssetLifecycleStatus = (typeof assetLifecycleStatuses)[number];

/**
 * Move an asset's lifecycle status.
 *
 * `reason` is REQUIRED for anything other than a return to `active`, and that
 * asymmetry is deliberate: retiring a unit ends its working life and
 * decommissioning it starts a billable strip-down, so both are decisions that
 * should explain themselves. Returning something to service needs no excuse.
 *
 * **Where the reason actually lands is worth knowing.** The database function
 * takes a `p_reason` parameter and never reads it — verified against the
 * function body, and true of `p_actor_role` too. So the reason is recorded by
 * the API's own `withAudit` envelope, which writes it into `audit_log`, rather
 * than by the routine. That is the real audit trail either way; the vestigial
 * parameters are left in place because removing them means DROP and re-CREATE
 * of a SECURITY DEFINER function plus re-granting EXECUTE, which is a bigger
 * and riskier change than the defect warrants. Recorded here so the next reader
 * does not assume the function is storing it.
 */
export const setAssetLifecycleBody = strictObject({
    status: z.enum(assetLifecycleStatuses),
    reason: z.string().trim().min(1).max(1000).nullish(),
    // The SHARED schema, not a local one: `replay.run` takes a required string,
    // and a locally-relaxed `.optional()` here typechecks at the parser and then
    // fails at the call site. Reusing it also keeps the minimum length in one
    // place — a two-character key is not a replay guard.
    idempotencyKey,
  })
  .refine((body) => body.status === "active" || Boolean(body.reason), {
    message: "retiring or decommissioning an asset needs a written reason",
    path: ["reason"],
  });
export type SetAssetLifecycleBody = z.output<typeof setAssetLifecycleBody>;

/** What the caller gets back: the state actually stored, read after the write. */
export interface AssetLifecycleReceipt {
  assetId: string;
  status: AssetLifecycleStatus | string;
  /** Set exactly when the asset is `retired`; cleared on the way back out. */
  retiredAt: string | null;
}
